/* MODLAND PDP v2: geometry only. Never intercepts scrolling, forms or cart requests. */
(() => {
  'use strict';
  if (!window.customElements || customElements.get('modland-pdp-layout')) return;
  const HEADER_ROOTS = '#shopify-section-header-inline, #shopify-section-header-sidebar, #shopify-section-header-bottom, #shopify-section-header-categories-menu, #shopify-section-header-vertical, #shopify-section-announcement-bar, #shopify-section-top-bar';
  class ModlandPdpLayout extends HTMLElement {
    connectedCallback() {
      if (this.active) return;
      this.root = this.closest('#modland-pdp-v2');
      if (!this.root) return;
      this.active = true;
      this.frame = 0;
      this.gap = this.number('gap', 16);
      this.fallback = this.number('headerFallback', 112);
      this.bottom = this.number('bottomClearance', 72);
      this.desktop = window.matchMedia('(min-width: 1025px)');
      this.queue = () => {
        if (!this.frame && this.active) this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.measure();
        });
      };
      this.rescan = () => { this.scanHeaders(); this.queue(); };
      this.anchorClick = (event) => {
        const link = event.target.closest('a[href^="#"]');
        if (!link || !this.root.contains(link)) return;
        const target = document.getElementById(link.getAttribute('href').slice(1));
        if (target && this.root.contains(target) && target.tagName === 'DETAILS') target.open = true;
        // The browser still performs its normal anchor navigation.
      };
      this.resizeObserver = window.ResizeObserver ? new ResizeObserver(this.queue) : null;
      this.scanHeaders();
      window.addEventListener('scroll', this.queue, { passive: true });
      window.addEventListener('resize', this.rescan, { passive: true });
      window.addEventListener('pageshow', this.rescan);
      document.addEventListener('shopify:section:load', this.rescan);
      document.addEventListener('shopify:section:reorder', this.rescan);
      this.root.addEventListener('click', this.anchorClick);
      this.root.addEventListener('toggle', this.queue, true);
      this.root.dataset.mlPdpReady = 'true';
      this.measure();
    }
    number(key, fallback) {
      const value = Number(this.dataset[key]);
      return Number.isFinite(value) && value >= 0 ? value : fallback;
    }
    scanHeaders() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      const roots = Array.from(document.querySelectorAll(HEADER_ROOTS));
      // Include header descendants, since Kalles can apply sticky positioning to an inner wrapper.
      this.headers = Array.from(new Set(roots.flatMap(el => [el, ...el.querySelectorAll('header, [data-header-sticky], .t4s-section-header, .t4s-header__wrapper, .t4s-header__inner, .t4s-header__sticky, .t4s-header__sticky-wrap')])));
      if (this.resizeObserver) this.headers.forEach(el => this.resizeObserver.observe(el));
    }
    setVariable(name, value) {
      if (this.root.style.getPropertyValue(name) !== value) this.root.style.setProperty(name, value);
    }
    measure() {
      if (!this.active || !this.root.isConnected) return;
      let clearance = 0;
      let reserve = this.fallback;
      for (const element of this.headers) {
        const style = getComputedStyle(element);
        if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) continue;
        const rect = element.getBoundingClientRect();
        if (!rect.width || !rect.height || rect.width < window.innerWidth / 2) continue;
        const positioned = style.position === 'fixed' || style.position === 'sticky';
        if (!positioned) continue;
        reserve = Math.max(reserve, rect.height + Math.max(0, parseFloat(style.top) || 0));
        if (rect.bottom > 0 && rect.top < Math.min(window.innerHeight * .4, 240)) {
          clearance = Math.max(clearance, rect.bottom);
        }
      }
      // Reserve space for the tallest measured sticky header, rather than resizing the image on each scroll.
      reserve = Math.min(Math.ceil(Math.max(reserve, clearance)), 320);
      const top = Math.ceil((clearance > 0 ? clearance : this.fallback) + this.gap);
      this.setVariable('--ml-pdp-header-reserve', `${reserve}px`);
      this.setVariable('--ml-pdp-sticky-top', `${top}px`);
      this.setVariable('--ml-pdp-gallery-position', this.dataset.sticky === 'true' ? 'sticky' : 'static');
      this.root.dataset.mlPdpCompact = String(this.desktop.matches && window.innerHeight - reserve - this.gap - this.bottom < 240);
    }
    disconnectedCallback() {
      if (!this.active) return;
      this.active = false;
      cancelAnimationFrame(this.frame);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      window.removeEventListener('scroll', this.queue);
      window.removeEventListener('resize', this.rescan);
      window.removeEventListener('pageshow', this.rescan);
      document.removeEventListener('shopify:section:load', this.rescan);
      document.removeEventListener('shopify:section:reorder', this.rescan);
      this.root.removeEventListener('click', this.anchorClick);
      this.root.removeEventListener('toggle', this.queue, true);
      delete this.root.dataset.mlPdpReady;
    }
  }
  customElements.define('modland-pdp-layout', ModlandPdpLayout);
})();
